import { useEffect, useState } from "react";
import { PoundSterling, Clock, MailCheck, Package, Loader2 } from "lucide-react";
import { fetchAdminDashboard, formatGBP } from "../lib/api";

const Stat = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <div
    data-testid={testid}
    className="border border-[#E2E8F0] bg-white p-5 flex flex-col gap-2"
  >
    <div className="flex items-center justify-between">
      <span className="sb-mono-label">{label}</span>
      <Icon className={`w-4 h-4 ${accent}`} strokeWidth={1.75} />
    </div>
    <div
      className="text-2xl sm:text-3xl font-semibold text-[#1B3A5C] tracking-tight"
      style={{ fontFamily: "Outfit" }}
    >
      {value}
    </div>
    {sub ? (
      <div className="font-mono text-[11px] uppercase tracking-wider text-[#64748B]">
        {sub}
      </div>
    ) : null}
  </div>
);

const OrdersDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchAdminDashboard()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading)
    return (
      <div
        data-testid="dashboard-loading"
        className="mb-8 flex items-center gap-2 text-sm text-[#64748B]"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading stats…
      </div>
    );

  if (!data) return null;

  const topSkuValue = data.top_sku_last_30d
    ? `${data.top_sku_last_30d.name}`
    : "—";
  const topSkuSub = data.top_sku_last_30d
    ? `${data.top_sku_last_30d.sku} · ${data.top_sku_last_30d.units} unit${
        data.top_sku_last_30d.units === 1 ? "" : "s"
      }`
    : "No completed orders in last 30 days";

  return (
    <div data-testid="orders-dashboard" className="mb-8">
      <div className="sb-mono-label mb-3">At a glance</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          testid="stat-paid-week"
          label="Paid · last 7 days"
          value={formatGBP(data.paid_this_week_gbp)}
          sub={`${data.paid_this_week_count} order${
            data.paid_this_week_count === 1 ? "" : "s"
          } · £${data.revenue_lifetime_gbp.toLocaleString("en-GB")} lifetime`}
          icon={PoundSterling}
          accent="text-[#047857]"
        />
        <Stat
          testid="stat-awaiting"
          label="Awaiting payment"
          value={data.awaiting_count}
          sub={`${data.reminders_sent_count} reminder${
            data.reminders_sent_count === 1 ? "" : "s"
          } emailed`}
          icon={Clock}
          accent="text-[#B91C1C]"
        />
        <Stat
          testid="stat-reminders"
          label="Reminders sent"
          value={data.reminders_sent_count}
          sub="Auto-swept hourly · 24h threshold"
          icon={MailCheck}
          accent="text-[#2C8C9C]"
        />
        <Stat
          testid="stat-top-sku"
          label="Top SKU · last 30 days"
          value={topSkuValue}
          sub={topSkuSub}
          icon={Package}
          accent="text-[#1B3A5C]"
        />
      </div>
    </div>
  );
};

export default OrdersDashboard;
